import React from 'react';
import { motion } from 'framer-motion';
import { FileX2, ScanLine, MailX, Timer, MessageSquareOff, RotateCcw, FileSearch, EyeOff, FileText, XCircle } from 'lucide-react';
const pains = [{
  icon: FileX2,
  text: 'Tardás horas armando un CV que nadie lee'
}, {
  icon: ScanLine,
  text: 'El 75% de los CVs son filtrados por IA antes de llegar a un humano'
}, {
  icon: MailX,
  text: 'Mandás postulaciones y el silencio es la única respuesta'
}, {
  icon: Timer,
  text: 'Los reclutadores descartan tu perfil en menos de 6 segundos'
}, {
  icon: MessageSquareOff,
  text: 'Llegás a una entrevista sin saber qué decir ni cuánto pedir'
}, {
  icon: RotateCcw,
  text: 'Cada semana empezás de cero sin un método que funcione'
}, {
  icon: FileSearch,
  text: 'No sabés cómo adaptar tu CV a cada búsqueda específica'
}, {
  icon: EyeOff,
  text: 'Tu LinkedIn no suma ni resta, simplemente no existe para los reclutadores'
}];
export function PainSection() {
  return <section className="py-16 relative overflow-hidden" data-id="element-510">
      <div className="container mx-auto px-4 relative z-10" data-id="element-511">
        {/* Document frame */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative max-w-4xl mx-auto" data-id="element-512">
          {/* File tab — sits on top of the card like a folder tab */}
          <div className="relative z-10 flex items-end gap-1.5 pl-6" data-id="element-513">
            <div className="bg-petroleum border-2 border-b-0 border-red-500/40 rounded-t-lg px-4 py-1.5 flex items-center gap-2" data-id="element-514">
              <FileText className="w-3.5 h-3.5 text-red-400" data-id="element-515" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-red-400" data-id="element-516">
                CV.pdf
              </span>
            </div>
            <span className="text-[10px] text-textsecondary/60 pb-2 hidden sm:inline" data-id="element-517">
              tu búsqueda actual
            </span>
          </div>

          {/* Document card */}
          <div className="relative bg-petroleum/80 border-2 border-red-500/40 rounded-2xl rounded-tl-none overflow-hidden shadow-2xl shadow-red-500/10" data-id="element-518">
            {/* Ruled-paper lines */}
            <div className="absolute inset-0 opacity-[0.035] pointer-events-none" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, #ffffff 0px, #ffffff 1px, transparent 1px, transparent 32px)'
          }} data-id="element-519"></div>
            {/* Left margin line (like a legal pad) */}
            <div className="absolute top-0 bottom-0 left-12 w-px bg-red-500/15 pointer-events-none hidden md:block" data-id="element-520"></div>

            {/* Rejection stamp — bottom right (out of heading area) */}
            <div className="absolute bottom-5 right-5 md:bottom-7 md:right-8 z-20 pointer-events-none select-none" style={{
            transform: 'rotate(-8deg)'
          }} data-id="element-521">
              <div className="border-[3px] border-red-500/70 rounded-lg px-3 py-1 md:px-4 md:py-1.5 bg-petroleum/60 backdrop-blur-sm" data-id="element-522">
                <span className="text-red-400/90 font-extrabold tracking-[0.2em] text-[10px] md:text-sm uppercase" data-id="element-523">
                  Sin respuestas
                </span>
              </div>
            </div>

            {/* Content padding */}
            <div className="relative p-6 md:p-10 md:pl-20" data-id="element-524">
              {/* Header */}
              <motion.div initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="mb-8 max-w-2xl" data-id="element-525">
                <div className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4" data-id="element-526">
                  <XCircle className="w-3 h-3" data-id="element-527" />
                  Reporte de tu búsqueda
                </div>

                <h2 className="text-2xl md:text-4xl font-extrabold mb-3 leading-tight" data-id="element-528">
                  ¿Cuántas semanas más vas a mandar CVs y esperar{' '}
                  <span className="text-red-400" data-id="element-529">en silencio?</span>
                </h2>

                <p className="text-sm md:text-base text-textsecondary leading-relaxed" data-id="element-530">
                  Mandás postulaciones, esperás respuestas que nunca llegan, y
                  cada día la angustia crece un poco más.
                </p>
                <p className="text-white font-semibold mt-2 text-sm md:text-base" data-id="element-531">
                  No es falta de experiencia. Es falta de un sistema.
                </p>
              </motion.div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-6" data-id="element-532">
                <div className="h-px flex-1 bg-red-500/20" data-id="element-533"></div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-red-400/70" data-id="element-534">
                  Diagnóstico
                </span>
                <div className="h-px flex-1 bg-red-500/20" data-id="element-535"></div>
              </div>

              {/* Pain list */}
              <div className="grid gap-2.5 md:grid-cols-2" data-id="element-536">
                {pains.map((pain, i) => {
                const Icon = pain.icon;
                return <motion.div key={i} initial={{
                  opacity: 0,
                  x: -8
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.04
                }} className="flex items-start gap-3 px-3.5 py-3 rounded-lg bg-graphite/40 border border-white/5 hover:border-red-500/30 hover:bg-red-500/[0.04] transition-colors" data-id="element-537">
                      <Icon className="w-4 h-4 text-red-400 shrink-0 mt-0.5" data-id="element-538" />
                      <span className="text-sm text-textsecondary leading-snug" data-id="element-539">
                        {pain.text}
                      </span>
                    </motion.div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Closing line */}
        <motion.h3 initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-xl md:text-2xl font-bold text-electric text-center mt-10" data-id="element-540">
          ¿Y si esta fuera la última semana que pasás así?
        </motion.h3>
      </div>
    </section>;
}